import {ServiceBase} from "../ServiceBase";
import {IWalletService} from "./wallet.service.interface";
import {makeObservable, observable, runInAction} from "mobx";
import {IServiceFactoryExtended} from "../service-factory-extended.interface";
import {BalanceType} from "../../../common/const/wallet_balance_type";
import {GameInfo} from "../games/game-info";
import {BankrollUpdateEventData} from "../firebase/topics/bankroll/bankroll-updates-topic.model.interface";

interface BankrollParts {
    cash: number;
    bonus: number;
    total: number;
}

export class WalletService extends ServiceBase implements IWalletService {
    constructor(serviceFactory: IServiceFactoryExtended) {
        super(serviceFactory);

        makeObservable(this);

        this.services.firebase.topics.bankroll.registerListener(eventData => {
            runInAction(() => {
                if(eventData.Currency !== this.services.currency.currencyIso3) {
                    this.services.sdk.eventsTrigger.currencyUpdated(eventData.Currency);
                }
                this._updatedBankroll = eventData;
                this.services.sdk.eventsTrigger.balanceUpdate(eventData.TotalBankroll); //this is still needed for keep alive (See USER_ACTIVITY_EVENTS_ALL)

            });
        });

        this.services.sdk.events.onOAuthStateChanged(async (params) => {
            (await this.services.loginPopup).showLoginGamePopupOnAuthStateChange(params)
        });

        this.reaction(() => this.services.user.isAuthorized, (isAuthorized) => {
            if(isAuthorized) {
                this.services.sdk.eventsTrigger.currencyUpdated(this._getWalletFromToken().CurrencyCode)
            }
        })

    }

    @observable.ref
    private _updatedBankroll: BankrollUpdateEventData | null = null;

    private get currentBankroll(): BankrollParts {
        if(!this.services.user.isAuthorized) {
            return {
                cash: 0,
                bonus: 0,
                total: 0
            };
        }

        if(this._updatedBankroll) {
            return {
                cash: this._updatedBankroll.RealMoneyBankroll,
                bonus: this._updatedBankroll.TotalBankroll - this._updatedBankroll.RealMoneyBankroll,
                total: this._updatedBankroll.TotalBankroll
            }
        }

        const walletFromToken = this._getWalletFromToken();
        return {
            cash: walletFromToken.RealMoneyBankroll,
            bonus: walletFromToken.AvailableMoney - walletFromToken.RealMoneyBankroll,
            total: walletFromToken.AvailableMoney
        }
    }

    private _getWalletFromToken(): {AvailableMoney: number; RealMoneyBankroll: number; CurrencyCode: string} {
        const walletDataJson = this.services.sdk.extractTokenProperty('walletData');
        if(!walletDataJson) {
            return {
                AvailableMoney: 0,
                RealMoneyBankroll: 0,
                CurrencyCode: this.services.currency.currencyIso3
            };
        }
        try {
            return JSON.parse(walletDataJson);
        }
        catch (err) {
            this.services.logger.error("Failed to parse walletData JSON", err, walletDataJson);
            return {
                AvailableMoney: 0,
                RealMoneyBankroll: 0,
                CurrencyCode: this.services.currency.currencyIso3
            };
        }
    }

    getFormattedBankrollByType(balanceType: BalanceType): string {
        switch (balanceType) {
            case BalanceType.BOTH:
                return this.services.currency.formatMoney(this.totalBankroll);
            case BalanceType.CASH:
                return this.services.currency.formatMoney(this.cashBankroll);
            case BalanceType.BONUS:
                return this.services.currency.formatMoney(this.bonusBankroll);
            default:
                throw new Error(`Unknown balance type: ${balanceType}`);

        }
    }

    get totalBankroll(): number {
        return this.currentBankroll.total;
    }

    get cashBankroll(): number {
        return this.currentBankroll.cash;
    }

    get bonusBankroll(): number {
        return this.currentBankroll.bonus;
    }


    computeGameMaxLimit(gameInfo: GameInfo): number {
        if(gameInfo.allowWithdrawOnlyFromReal) {
            return this.cashBankroll;
        } else {
            return this.totalBankroll;
        }
    }

}
